import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Shuffle, Mail, Phone, User, Shield, HardHat } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const CreateUserModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'customer',
    engineer_type: '',
    password: ''
  });
  const [creating, setCreating] = useState(false);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  const handleRoleChange = (value) => {
    setFormData({ 
      ...formData, 
      role: value,
      engineer_type: value === 'engineer' ? 'construction' : ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const submitData = { ...formData };
      // Only include engineer_type for engineers
      if (formData.role !== 'engineer') {
        delete submitData.engineer_type;
      }
      
      await api.post('/auth/register', submitData);
      toast.success(
        <div className="space-y-1">
          <p className="font-medium">User created successfully</p>
          <p className="text-sm text-stone-600">Temporary password: <code className="bg-stone-100 px-1.5 py-0.5 rounded">{formData.password}</code></p>
        </div>
      );
      setFormData({ name: '', email: '', phone: '', role: 'customer', engineer_type: '', password: '' });
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', phone: '', role: 'customer', engineer_type: '', password: '' });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-white border-0 shadow-2xl rounded-lg">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900">Add New User</DialogTitle>
          <DialogDescription className="text-stone-500">
            Create a new customer or site engineer account
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2" data-testid="create-user-form">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-stone-700 flex items-center">
              <User className="h-4 w-4 mr-2 text-stone-400" />
              Full Name
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              className="h-11 border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg"
              required
              data-testid="user-name-input"
            />
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-stone-700 flex items-center">
              <Mail className="h-4 w-4 mr-2 text-stone-400" />
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              className="h-11 border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg"
              required
              data-testid="user-email-input"
            />
          </div>

          {/* Phone Field */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium text-stone-700 flex items-center">
              <Phone className="h-4 w-4 mr-2 text-stone-400" />
              Mobile Number
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+91 98765 43210"
              className="h-11 border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg"
              required
              data-testid="user-phone-input"
            />
          </div>

          {/* Role Field */}
          <div className="space-y-2">
            <Label htmlFor="role" className="text-sm font-medium text-stone-700 flex items-center">
              <Shield className="h-4 w-4 mr-2 text-stone-400" />
              User Role
            </Label>
            <Select
              value={formData.role}
              onValueChange={handleRoleChange}
              required
            >
              <SelectTrigger id="role" className="h-11 border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg" data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer (Homeowner)</SelectItem>
                <SelectItem value="engineer">Engineer</SelectItem>
                <SelectItem value="admin">Admin (Co-Admin)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Engineer Type Field - Only shown for engineers */}
          {formData.role === 'engineer' && (
            <div className="space-y-2">
              <Label htmlFor="engineer_type" className="text-sm font-medium text-stone-700 flex items-center">
                <HardHat className="h-4 w-4 mr-2 text-stone-400" />
                Engineer Type
              </Label>
              <Select
                value={formData.engineer_type}
                onValueChange={(value) => setFormData({ ...formData, engineer_type: value })}
                required
              >
                <SelectTrigger id="engineer_type" className="h-11 border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg" data-testid="engineer-type-select">
                  <SelectValue placeholder="Select engineer type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="design">Design Engineer</SelectItem>
                  <SelectItem value="construction">Construction Engineer</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500">
                Design Engineers handle architectural plans and drawings. Construction Engineers manage on-site work.
              </p>
            </div>
          )}

          {/* Password Field */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-stone-700">
              Temporary Password
            </Label>
            <div className="flex space-x-2">
              <Input
                id="password"
                type="text"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Click generate"
                className="h-11 border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg font-mono"
                required
                data-testid="user-password-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={generatePassword}
                className="h-11 px-4 border-stone-200 hover:bg-stone-50 rounded-lg"
                data-testid="generate-password-button"
              >
                <Shuffle className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
            <p className="text-sm text-stone-600 leading-relaxed">
              Share the temporary password securely with the user. They should change it after their first login.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-11 border-stone-200 text-stone-700 hover:bg-stone-50 rounded-lg"
              disabled={creating}
              data-testid="cancel-create-user"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-11 bg-stone-900 hover:bg-stone-800 text-white rounded-lg"
              disabled={creating}
              data-testid="submit-create-user"
            >
              {creating ? (
                <span className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Creating...
                </span>
              ) : (
                <span className="flex items-center">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create User
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;
